import { ApiClient } from '@api/api-configuration';
import type { OverviewSummaryResponse } from '@api/READ_ONLY/billing_api/Api';
import { ArrayOfAvailableSupportedCurrencies, DEFAULT_CURRENCY } from '@app/common/constants';
import { BILLING_QUERY_KEYS } from '@app/common/queryKeys';
import Text from '@app/components/ui/Text/Text';
import type { SupportedCurrencies } from '@app/utils/pricingCurrencyUtils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Select } from 'antd';
import dayjs from 'dayjs';
import { useMemo, useState, type ReactNode } from 'react';
import FinanceIncomeExpenses from '../FinanceIncomeExpenses/FinanceIncomeExpenses';
import type { FinanceIncomeExpensesPeriod } from '../FinanceIncomeExpenses/types';
import KpiResults from '../KpiResults/KpiResults';
import styles from './FinancialIncomeAndKpis.module.css';

const PERIOD_STATES = {
  THIS_MONTH: 'this-month',
  PREVIOUS_MONTH: 'previous-month',
  THREE_MONTHS: '3-months',
  YEAR: 'year',
} as const satisfies Record<string, FinanceIncomeExpensesPeriod>;

const PERIOD_OPTIONS: Array<{ value: FinanceIncomeExpensesPeriod; label: ReactNode }> = [
  { value: PERIOD_STATES.THIS_MONTH, label: <Text type="body2" transKey="period-this-month" /> },
  { value: PERIOD_STATES.PREVIOUS_MONTH, label: <Text type="body2" transKey="period-previous-month" /> },
  { value: PERIOD_STATES.THREE_MONTHS, label: <Text type="body2" transKey="period-3-months" /> },
  { value: PERIOD_STATES.YEAR, label: <Text type="body2" transKey="period-year" /> },
];

const toBillingRangeDateTime = (value: dayjs.Dayjs) => value.format('YYYY-MM-DDTHH:mm:ss');

const buildPeriodDateRange = (period: FinanceIncomeExpensesPeriod) => {
  const currentMonth = dayjs.tz();

  if (period === PERIOD_STATES.PREVIOUS_MONTH) {
    const previousMonth = currentMonth.subtract(1, 'month');
    return {
      startAt: toBillingRangeDateTime(previousMonth.startOf('month')),
      endAt: toBillingRangeDateTime(previousMonth.endOf('month')),
    };
  }

  if (period === PERIOD_STATES.THREE_MONTHS) {
    return {
      startAt: toBillingRangeDateTime(currentMonth.subtract(2, 'month').startOf('month')),
      endAt: toBillingRangeDateTime(currentMonth.endOf('month')),
    };
  }

  if (period === PERIOD_STATES.YEAR) {
    return {
      startAt: toBillingRangeDateTime(currentMonth.subtract(11, 'month').startOf('month')),
      endAt: toBillingRangeDateTime(currentMonth.endOf('month')),
    };
  }

  return {
    startAt: toBillingRangeDateTime(currentMonth.startOf('month')),
    endAt: toBillingRangeDateTime(currentMonth.endOf('month')),
  };
};

const FinancialIncomeAndKpis = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<FinanceIncomeExpensesPeriod>(PERIOD_STATES.THREE_MONTHS);
  const periodRange = buildPeriodDateRange(selectedPeriod);

  const {
    data: summaryData,
    isLoading: isSummaryInitialLoading,
    isFetching: isSummaryFetching,
  } = useQuery<OverviewSummaryResponse>({
    // Centralized query keys keep billing cache entries consistent across screens.
    queryKey: [BILLING_QUERY_KEYS.OVERVIEW_SUMMARY, periodRange.startAt, periodRange.endAt],
    queryFn: async () => {
      const response = await ApiClient.billingApi.billing.getBillingOverviewSummary(periodRange);
      return response.data;
    },
    placeholderData: keepPreviousData,
  });

  const isSummaryLoading = isSummaryInitialLoading || isSummaryFetching;
  const responseCurrency = summaryData?.currency;

  const currency: SupportedCurrencies =
    responseCurrency && ArrayOfAvailableSupportedCurrencies.includes(responseCurrency as SupportedCurrencies)
      ? (responseCurrency as SupportedCurrencies)
      : DEFAULT_CURRENCY;

  const kpis = summaryData?.kpis;
  const totalAcquired = kpis?.income?.totalAcquired ?? 0;
  const totalIncomeInvoiced = kpis?.income?.totalIncomeInvoiced ?? 0;
  const doctorPayables = kpis?.doctorPayables ?? 0;
  const caregiverPayables = kpis?.caregiverPayables ?? 0;
  const openClientBalances = kpis?.openClientBalances ?? 0;
  const netCashPosition = kpis?.netCashPosition ?? 0;

  const incomeExpensesRows = useMemo(
    () =>
      (summaryData?.incomeExpenses ?? []).map((row) => {
        const month = dayjs(row.month);

        return {
          key: row.key,
          month: month.isValid() ? month : row.month,
          income: row.income ?? 0,
          doctors: row.doctors ?? 0,
          caregivers: row.caregivers ?? 0,
        };
      }),
    [summaryData?.incomeExpenses]
  );

  return (
    <section>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <Text type="h4" transKey="billing-overview-kpi-section-title" />

          <Text type="body2" transKey="billing-overview-kpi-section-subtitle" color="#64748b" />
        </div>

        <div className={styles.periodControl}>
          <Text type="label2" transKey="period" color="#64748b" className={styles.periodLabel} />

          <Select<FinanceIncomeExpensesPeriod>
            className={styles.periodSelect}
            value={selectedPeriod}
            onChange={setSelectedPeriod}
            options={PERIOD_OPTIONS}
          />
        </div>
      </div>

      <KpiResults
        isLoading={isSummaryLoading}
        currency={currency}
        totalAcquired={totalAcquired}
        totalIncomeInvoiced={totalIncomeInvoiced}
        doctorPayables={doctorPayables}
        caregiverPayables={caregiverPayables}
        openClientBalances={openClientBalances}
        netCashPosition={netCashPosition}
      />

      <div className={styles.financeSection}>
        <FinanceIncomeExpenses
          data={incomeExpensesRows}
          period={selectedPeriod}
          currency={currency}
          isLoading={isSummaryLoading}
        />
      </div>
    </section>
  );
};

export default FinancialIncomeAndKpis;
